import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

export default function BlogDetailPage() {
  const { t, i18n } = useTranslation();
  
  // تحديد اللغة الحالية والاتجاهات ديناميكياً
  const currentLang = i18n.language || localStorage.getItem('lang') || 'ar';
  const isRtl = currentLang === 'ar';
  const pageDirection = isRtl ? 'rtl' : 'ltr';
  const textAlignment = isRtl ? 'text-right' : 'text-left';
  const { id } = useParams(); // جلب معرف المقال من الرابط
  
  const [blog, setBlog] = useState(null);
  const [latestArticles, setLatestArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      setError(null);
      try {
        // جلب تفاصيل المقال الحالي وأحدث المقالات بالتوازي لسرعة الأداء
        const [blogResponse, latestResponse] = await Promise.all([
          fetch(`https://ba7ary.draconiccode.com/public/api/blogs/${id}`),
          fetch('https://ba7ary.draconiccode.com/public/api/blogs')
        ]);

        if (!blogResponse.ok) {
          throw new Error(t('error_fetching_blog'));
        }

        const blogResult = await blogResponse.json();
        setBlog(blogResult.data);

        // معالجة أحدث المقالات وعرض آخر 5 فقط في الشريط الجانبي
        if (latestResponse.ok) {
          const latestResult = await latestResponse.json();
          if (latestResult.data && Array.isArray(latestResult.data)) {
            setLatestArticles(latestResult.data.slice(0, 5));
          }
        }
      } catch (err) {
        setError(err.message || t('error_fetching_blog'));
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAllData();
    }
  }, [id, currentLang, t]);

  // دالة تنسيق التاريخ بناءً على اللغة المفعلة
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    
    // تحديد صيغة المنطقة بناءً على اللغات الخمس المدعومة
    let locale = 'en-US';
    if (currentLang === 'ar') locale = 'ar-EG';
    else if (currentLang === 'de') locale = 'de-DE';
    else if (currentLang === 'fr') locale = 'fr-FR';
    else if (currentLang === 'tr') locale = 'tr-TR';

    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 1. حالة التحميل (تستخدم ملفات الترجمة الآن بشكل كامل)
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600 font-medium text-lg animate-pulse">
          {t('loading')}
        </div>
      </div>
    );
  }

  // 2. حالة الخطأ أو عدم وجود مقال
  if (error || !blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4" dir={pageDirection}>
        <div className="text-red-600 font-medium text-lg mb-4">{error}</div>
        <Link to="/blogs" className="px-5 py-2 bg-blue-900 text-white rounded-lg text-sm font-medium hover:bg-blue-950 transition">
          {t('back_to_blogs')}
        </Link>
      </div>
    );
  }

  // استخراج العنوان الرئيسي المترجم (مع لغة احتياطية ar)
  const blogTitle = blog.title?.[currentLang] || blog.title?.['ar'] || '';

  return (
    <div className={`bg-slate-50 min-h-screen ${textAlignment} pb-16 transition-all duration-300`} dir={pageDirection}>
      
      {/* البانر العلوي (Hero Section) */}
      <section className="relative w-full h-[65vh] bg-slate-900 overflow-hidden flex items-end">
        {blog.banner_image && (
          <img 
            src={blog.banner_image} 
            alt={blogTitle} 
            className="absolute inset-0 w-full h-full object-cover opacity-50"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/50 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto w-full px-4 pb-12 relative z-10 text-white">
          <h1 className="text-2xl md:text-5xl font-extrabold leading-tight mb-6 max-w-4xl">
            {blogTitle}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-300">
            <span>{formatDate(blog.created_at)}</span>
          </div>
        </div>
      </section>

      {/* محتوى الصفحة (شريط جانبي + المقال الرئيسي) */}
      <div className="max-w-7xl mx-auto px-4 mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* أ) الشريط الجانبي (Sidebar) */}
        <aside className="flex flex-col gap-6">
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <h4 className="font-bold text-slate-900 mb-4 pb-2 border-b border-gray-100">
              {t('latest_articles')}
            </h4>
            <div className="flex flex-col gap-4">
              {latestArticles.length > 0 ? (
                latestArticles.map((article) => {
                  const articleTitle = article.title?.[currentLang] || article.title?.['ar'] || '';
                  return (
                    <Link 
                      key={article.id} 
                      to={`/blogs/${article.id}`} 
                      className="flex gap-3 items-center group cursor-pointer"
                    >
                      <div className="w-16 h-16 bg-slate-100 rounded-lg flex-shrink-0 overflow-hidden group-hover:opacity-80 transition border border-gray-50">
                        {article.banner_image && (
                          <img 
                            src={article.banner_image} 
                            alt={articleTitle} 
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div>
                        <h5 className="text-sm font-bold text-slate-800 group-hover:text-blue-900 transition line-clamp-2">
                          {articleTitle}
                        </h5>
                        <span className="text-xs text-gray-400 mt-1 block">
                          {formatDate(article.created_at)}
                        </span>
                      </div>
                    </Link>
                  );
                })
              ) : (
                <div className="text-xs text-gray-400 text-center py-2">
                  {t('no_latest_articles')}
                </div>
              )}
            </div>
          </div>
        </aside>
        
        {/* ب) المحتوى الرئيسي المترجم ديناميكياً (Main Content) */}
        <main className="lg:col-span-2 flex flex-col gap-8">
          {blog.paragraphs && blog.paragraphs.length > 0 ? (
            blog.paragraphs.map((paragraph) => {
              const pTitle = paragraph.title?.[currentLang] || paragraph.title?.['ar'] || '';
              const pContent = paragraph.content?.[currentLang] || paragraph.content?.['ar'] || '';

              return (
                <div key={paragraph.id} className="flex flex-col gap-4 bg-white p-6 rounded-2xl border border-gray-50 shadow-sm">
                  
                  {pTitle && (
                    <h2 className="text-xl md:text-2xl font-bold text-slate-900">
                      {pTitle}
                    </h2>
                  )}

                  {paragraph.image && (
                    <div className="w-full h-80 md:h-[400px] rounded-xl overflow-hidden shadow-sm bg-slate-200 my-2">
                      <img 
                        src={paragraph.image} 
                        alt={pTitle || "Paragraph image"} 
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  
                  {pContent && (
                    <p className="text-slate-600 text-base leading-relaxed whitespace-pre-line">
                      {pContent}
                    </p>
                  )}

                </div>
              );
            })
          ) : (
            <div className="text-gray-500 text-center py-8">
              {t('no_content_available')}
            </div>
          )}
        </main>

      </div>
    </div>
  );
}